import { Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

type Acesso = { id: string; motivo: string; criadaEm: Date | string; expiraEm: Date | string; encerradaEm: Date | string | null };

const dataHora = (d: Date | string) =>
  new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const minutos = (a: Acesso) =>
  Math.max(1, Math.round((new Date(a.encerradaEm ?? a.expiraEm).getTime() - new Date(a.criadaEm).getTime()) / 60_000));

/** A pessoa libera (72 h) ou revoga o "ver como" do suporte, e vê cada acesso feito. */
export function AcessoDoSuporte({
  liberadoAte,
  acessos,
  botao,
}: {
  liberadoAte: Date | string | null;
  acessos: Acesso[];
  botao: string;
}) {
  const utils = trpc.useUtils();
  const opcoes = {
    onSuccess: () => utils.privacidade.resumo.invalidate(),
    onError: (e: { message: string }) => toast.error(e.message),
  };
  const liberar = trpc.privacidade.liberarSuporte.useMutation({ ...opcoes, onSuccess: () => { toast.success("Acesso do suporte liberado por 72 horas."); opcoes.onSuccess(); } });
  const revogar = trpc.privacidade.revogarSuporte.useMutation({ ...opcoes, onSuccess: () => { toast.success("Acesso do suporte revogado."); opcoes.onSuccess(); } });
  const ocupado = liberar.isPending || revogar.isPending;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 max-w-xl">
          <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">Acesso do suporte</p>
          <p className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            {liberadoAte
              ? `Liberado até ${dataHora(liberadoAte)}. O suporte pode abrir sua conta em modo leitura — sem alterar nada.`
              : "Para o suporte ver a sua tela e ajudar, libere o acesso por 72 horas. É só leitura, e você revoga quando quiser."}
          </p>
        </div>
        {liberadoAte ? (
          <button type="button" className={botao} disabled={ocupado} onClick={() => revogar.mutate()}>
            {revogar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Revogar agora
          </button>
        ) : (
          <button type="button" className={botao} disabled={ocupado} onClick={() => liberar.mutate()}>
            {liberar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            Permitir acesso por 72 h
          </button>
        )}
      </div>
      {acessos.length > 0 && (
        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 text-[12.5px] dark:divide-border dark:border-border">
          {acessos.map((a) => (
            <li key={a.id} className="flex flex-wrap justify-between gap-2 px-3 py-2">
              <span className="text-zinc-900 dark:text-foreground">
                {dataHora(a.criadaEm)} · {a.motivo}
              </span>
              <span className="text-zinc-500 dark:text-muted-foreground">
                {a.encerradaEm || new Date(a.expiraEm) < new Date() ? `${minutos(a)} min` : "em andamento"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
